import {useCallback, useEffect, useRef, useState, type RefObject} from 'react';
import type {WebcamRefTypes} from '@chekinapp/ui';

export const LUMA_TOO_DARK = 34;
export const LUMA_BRIGHT_ENOUGH = 44;

const SAMPLE_INTERVAL_MS = 500;
const SAMPLE_SIZE = 64;
const MAX_FAILED_SAMPLES = 4;

export const averageLuma = (data: Uint8ClampedArray): number => {
  if (data.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  }
  return sum / (data.length / 4);
};

type GateState = 'idle' | 'checking' | 'dark' | 'passed';

type UseLightingGateProps = {
  videoRef: RefObject<WebcamRefTypes | null>;
};

export const useLightingGate = ({videoRef}: UseLightingGateProps) => {
  const [gateState, setGateState] = useState<GateState>('idle');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const failedSamplesRef = useRef(0);

  const sampleLuma = useCallback((): number | null => {
    const video = videoRef.current?.video;
    if (!video || video.videoWidth === 0) return null;

    try {
      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      const canvas = canvasRef.current;
      canvas.width = SAMPLE_SIZE;
      canvas.height = SAMPLE_SIZE;
      const context = canvas.getContext('2d', {willReadFrequently: true});
      if (!context) return null;
      context.drawImage(video, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
      return averageLuma(context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data);
    } catch {
      return null;
    }
  }, [videoRef]);

  const startLightingGate = useCallback(() => {
    failedSamplesRef.current = 0;
    setGateState('checking');
  }, []);

  useEffect(() => {
    if (gateState !== 'checking' && gateState !== 'dark') return;

    const tick = () => {
      const luma = sampleLuma();

      if (luma == null) {
        failedSamplesRef.current += 1;
        if (failedSamplesRef.current >= MAX_FAILED_SAMPLES) setGateState('passed');
        return;
      }
      failedSamplesRef.current = 0;

      setGateState(previous => {
        if (luma >= LUMA_BRIGHT_ENOUGH) return 'passed';
        if (luma < LUMA_TOO_DARK) return 'dark';
        return previous === 'dark' ? 'dark' : 'passed';
      });
    };

    tick();
    const interval = setInterval(tick, SAMPLE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [gateState, sampleLuma]);

  return {
    isTooDark: gateState === 'dark',
    isGatePassed: gateState === 'passed',
    startLightingGate,
  };
};
